'use client';

import { useRouter, usePathname } from 'next/navigation';
import { useEffect, useState, useRef } from 'react';
import { useQuery } from '@tanstack/react-query';
import { useAuth } from '../context/AuthContext';
import Header from '@/components/header';
import { SidebarInset, SidebarProvider } from '@/components/ui/sidebar';
import { AppSidebar } from '@/components/app-sidebar';
import axiosInstance from '@/lib/axios';
import React from 'react';
import { showToast } from '@/components/ui/toast';
import { useSocket } from '@/providers/SocketProvider';
import { queryClient } from '@/lib/queryClient';
import { Toaster } from './ui/sonner';
import { Loader } from './ui/loader';

export default function AuthWrapper({ children }: { children: React.ReactNode }) {
  const router = useRouter();
  const pathname = usePathname();
  const { isAuthenticated, loading } = useAuth();
  const [setupChecked, setSetupChecked] = useState(false);
  const [userData, setUserData] = useState<any>(null);
  const [importProgress, setImportProgress] = useState(0);
  const [exportProgress, setExportProgress] = useState(0);
  const isDownloadPage = pathname?.startsWith('/invoices/download');
  const hasRedirectedRef = useRef(false); // ✅ Prevent multiple redirects

  const { socket } = useSocket();

  // Setup check
  useEffect(() => {
    const checkSetup = async () => {
      try {
        const response = await axiosInstance.get('/api/setup');
        const { setupComplete } = response.data;

        if (!setupComplete && pathname !== '/setup') {
          router.replace('/setup');
        } else if (setupComplete) {
          const token = localStorage.getItem('token');
          if (!token && pathname !== '/login' && !isDownloadPage) {
            router.replace('/login');
          }
        }

        setSetupChecked(true);
      } catch (error) {
        console.error('Error checking setup status:', error);
        setSetupChecked(true); // allow fallback
      }
    };

    checkSetup();
  }, [pathname, router]);

  // Fetch user data
  const { data, isLoading: userDataLoading } = useQuery({
    queryKey: ['userData'],
    queryFn: async () => {
      const response = await axiosInstance.get('/api/auth/me');
      return response.data;
    },
    enabled: isAuthenticated,
    retry: false,
    staleTime: 0,
  });

  useEffect(() => {
    if (data?._id) {
      localStorage.setItem('userId', data._id);
    }
  }, [data]);

  useEffect(() => {
    if (isAuthenticated && data?._id) {
      setUserData(data);
      const userId = localStorage.getItem('userId');
      if (userId === data._id) {
        socket?.emit('subscribe', 'notifications');
      }
    }
  }, [isAuthenticated, data, socket]);

  // Real-time event listeners
  useEffect(() => {
    if (!socket) return;

    socket.on('importComplete', (data) => {
      setImportProgress(100);
      showToast({
        message: `Import Complete: ${data.processed}/${data.total}`,
        type: 'success',
        dismissible: true,
        closeButton: true,
      });
      if (data.errors?.length > 0) {
        showToast({
          message: `${data.errors.length} import errors occurred`,
          type: 'warning',
          dismissible: true,
          closeButton: true,
        });
      }
      queryClient.invalidateQueries('categories' as any);
    });

    socket.on('importError', (data) => {
      setImportProgress(0);
      showToast({
        message: `Import Error: ${data.error}`,
        type: 'error',
        dismissible: true,
        closeButton: true,
      });
    });

    socket.on('exportProgress', (data) => {
      setExportProgress(data.progress);
      showToast({
        message: `Export Progress: ${data.progress}%`,
        type: 'info',
        dismissible: true,
        closeButton: true,
      });
    });

    socket.on('exportComplete', (data) => {
      setExportProgress(100);
      showToast({
        message: 'Export Complete',
        type: 'success',
        dismissible: true,
        closeButton: true,
      });

      if (data.data) {
        const blob = new Blob([data.data], { type: 'text/csv' });
        const url = URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = 'categories.csv';
        a.click();
        URL.revokeObjectURL(url);
      }
    });

    socket.on('exportError', (data) => {
      setExportProgress(0);
      showToast({
        message: `Export Error: ${data.error}`,
        type: 'error',
        dismissible: true,
        closeButton: true,
      });
    });

    return () => {
      socket.off('importComplete');
      socket.off('importError');
      socket.off('exportProgress');
      socket.off('exportComplete');
      socket.off('exportError');
    };
  }, [socket]);

  // Redirect logic with protection
  useEffect(() => {
    if (!setupChecked || loading || hasRedirectedRef.current) return;

    if (
      !isAuthenticated &&
      pathname !== '/login' &&
      pathname !== '/setup' &&
      !isDownloadPage
    ) {
      hasRedirectedRef.current = true;
      router.replace('/login');
    } else if (
      isAuthenticated &&
      (pathname === '/login' || pathname === '/setup')
    ) {
      hasRedirectedRef.current = true;
      router.replace('/dashboard');
    }
  }, [setupChecked, loading, isAuthenticated, pathname, isDownloadPage, router]);

  // Prevent premature layout render
  if (!setupChecked || loading) {
    return <Loader />;
  }

  // ✅ Show layout only if authenticated and not a download/login/setup page
  const showLayout =
    isAuthenticated &&
    pathname !== '/login' &&
    pathname !== '/setup' &&
    !isDownloadPage;

  if (showLayout) {
    return (
      <div className="flex h-screen overflow-hidden bg-background pb-4">
        <SidebarProvider
          style={{ '--sidebar-width': '18rem' } as React.CSSProperties}
          defaultOpen={typeof window !== 'undefined' && window.innerWidth >= 1024}
        >
          <AppSidebar user={userData} />
          <SidebarInset>
            <Header />
            <Toaster position="top-right" richColors closeButton />
            <main className="flex overflow-y-auto">{children}</main>
          </SidebarInset>
        </SidebarProvider>
      </div>
    );
  }

  // 🔓 Show children directly (login/setup/download)
  return <>{children}</>;
}
